import { useEffect, useRef, useState } from "react";

const THRESHOLD = 72;
const MAX_PULL = 110;

export default function PullToRefresh({ onRefresh }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const startY = useRef(null);
  const busyRef = useRef(false);
  const pullRef = useRef(0);
  const cb = useRef(onRefresh);
  cb.current = onRefresh;

  useEffect(() => {
    if (!window.matchMedia("(pointer: coarse)").matches) return;
    const atTop = () => (document.scrollingElement || document.documentElement).scrollTop <= 0;
    const setP = (v) => { pullRef.current = v; setPull(v); };
    const onStart = (e) => {
      startY.current = busyRef.current || !atTop() ? null : e.touches[0].clientY;
    };
    const onMove = (e) => {
      if (startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0 || !atTop()) { setP(0); return; }
      setP(Math.min(dy * 0.4, MAX_PULL));
    };
    const onEnd = async () => {
      if (startY.current == null) return;
      startY.current = null;
      if (pullRef.current >= THRESHOLD) {
        busyRef.current = true;
        setBusy(true);
        try { await cb.current?.(); } finally {
          busyRef.current = false;
          setBusy(false);
        }
      }
      setP(0);
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const prog = Math.min(pull / THRESHOLD, 1);
  if (!busy && pull <= 6) return null;
  return (
    <div
      className="md:hidden fixed left-1/2 z-40 pointer-events-none transition-opacity"
      style={{ top: 66 + pull * 0.35, transform: "translateX(-50%)", opacity: busy ? 1 : prog }}
      data-testid="pull-to-refresh"
    >
      <div className="w-9 h-9 rounded-full bg-surface border border-hairline shadow-raised flex items-center justify-center">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          className={busy ? "animate-spin" : ""}
          style={busy ? undefined : { transform: `rotate(${prog * 270}deg)` }}
        >
          <circle cx="12" cy="12" r="8.5" fill="none" stroke="#EDE5D8" strokeWidth="2.5" />
          <path d="M12 3.5 A 8.5 8.5 0 0 1 20.5 12" fill="none" stroke="#C15A38" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
